import Modal, { ModalFooter } from "@amzn/meridian/modal";
import React, { useState, useEffect } from "react";
import Row from "@amzn/meridian/row";
import Button from "@amzn/meridian/button";
import Text from "@amzn/meridian/text";
import Textarea from "@amzn/meridian/textarea";
import Column from "@amzn/meridian/column";
import { Space } from "../../../space/Space";
import { UpdateAppealRequest, useUpdateAppealMutation } from "../../../../endpoints/updateAppeal";
import { useAppSelector } from "../../../../app/store/hooks";
import { IssuesModalProps } from "./types";

export const IssuesModal: React.FC<IssuesModalProps> = ({
    open,
    onClose,
    initialData = [],
    immutableInitialData,
}) => {
    const [keyIssues, setKeyIssues] = useState<string[]>(initialData.length ? initialData : [""]);
    const [updateAppeal, { isLoading }] = useUpdateAppealMutation();
    const { alias } = useAppSelector(state => state.auth.user);

    useEffect(() => {
        if (initialData.length) {
            setKeyIssues(initialData);
        }
    }, [initialData]);

    const handleTextChange = (index: number, value: string) => {
        const newIssues = [...keyIssues];
        newIssues[index] = value;
        setKeyIssues(newIssues);
    };

    const handleAddIssue = () => {
        setKeyIssues([...keyIssues, ""]);
    };

    const handleRemoveIssue = (index: number) => {
        const newIssues = keyIssues.filter((_, i) => i !== index);
        setKeyIssues(newIssues.length ? newIssues : [""]);
    };

    const handleSave = async () => {
        try {
            const filteredIssues = keyIssues.filter(issue => issue.trim() !== "");

            const updateData: UpdateAppealRequest = {
                businessUseCase: "SMART",
                appealArtifactId: immutableInitialData.appealArtifactId,
                appealDetails: {
                    ...immutableInitialData.appealDetails,
                    appealApproachCalculationData: {
                        ...immutableInitialData.appealDetails.appealApproachCalculationData,
                        exportAppealsPackage: {
                            ...immutableInitialData.appealDetails.appealApproachCalculationData
                                ?.exportAppealsPackage,
                            keyIssues: filteredIssues,
                        },
                    },
                },
                appealStatus: immutableInitialData.appealDetails.status,
                clientName: alias,
            };

            const result = await updateAppeal(updateData).unwrap();
            if (result.status === "SUCCESS") {
                onClose();
            } else {
                console.error("Failed to update key issues");
            }
        } catch (error) {
            console.error("Error updating key issues:", error);
        } finally {
            onClose();
        }
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Edit Key Issues"
            width="30vw"
            aria-describedby="EditKeyIssuesModal"
        >
            <Column>
                <Text>Key Issues</Text>
                {keyIssues.map((issue, index) => (
                    <Row key={index} alignmentVertical="center">
                        <Column width="100%">
                            <Space size="s" />
                            <Row alignmentVertical="center" alignmentHorizontal="justify">
                                <Text type="h100">Issue {index + 1}</Text>
                                {keyIssues.length > 1 && (
                                    <Button
                                        type="tertiary"
                                        size="small"
                                        onClick={() => handleRemoveIssue(index)}
                                        aria-label={`Remove Issue ${index + 1}`}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </Row>
                            <Textarea
                                value={issue}
                                onChange={value => handleTextChange(index, value)}
                                placeholder={`Enter Issue ${index + 1}`}
                                rows={3}
                            />
                        </Column>
                    </Row>
                ))}
                <Space size="s" />
                <Button type="tertiary" size="small" onClick={handleAddIssue}>
                    Add New Issue
                </Button>
            </Column>
            <Space size="s" />
            <ModalFooter>
                <Row alignmentHorizontal="justify">
                    <Button type="secondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={isLoading || !keyIssues.some(issue => issue.trim() !== "")}
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </Button>
                </Row>
            </ModalFooter>
        </Modal>
    );
};
